import { CookedCategory, ModelMaterial } from '@/types/dmaker/category.type'
import { Resource } from '@/types/dmaker/resource.type'
import { ModelResId } from '@/store/creative/model-editor/index.type'
import _ from 'lodash'
import {
  getTargetTextureResourceIndex,
  isTextureResource,
} from '@/views/creative/utils/index'
import Vue from 'vue'
import { createFeModelId, isFeModelId } from '@/store/creative/common/category'

/**
 * 赋值默认材质列表，只有 resource 中含有对应贴图资源时才会填充，否则不填充。通过 prefix 来判断是否是对应的贴图
 * @param category
 * @param resource
 */
export function assignDefaultMaterialToProperties({
  category,
  resource,
}: {
  category: CookedCategory
  resource: { [key: number]: Resource[] }
}): void {
  if (category.properties?.model_material_list?.length) {
    console.log('当前的模型坑位已经有材质，不需要填充')
    return
  }

  // 筛选出包含贴图资源的项
  const textureResourceList = Object.values(resource)
    .flat()
    .filter(isTextureResource)

  // 获取默认贴图前缀
  const getDefaultTexturePrefixByIndex = (index: number) => {
    return (
      textureResourceList.find(
        (res) => getTargetTextureResourceIndex(res, category.prefix) === index,
      )?.prefix || ''
    )
  }

  // 检查是否存在至少一个默认贴图资源
  const hasDefaultTexture = Array.from({ length: 6 }, (_, i) =>
    getDefaultTexturePrefixByIndex(i),
  ).some((prefix) => prefix)

  if (hasDefaultTexture) {
    const newMaterial = {
      id: createFeModelId(),
      is_default: true,
      name: `皮肤 1`,
      name_en: `skin 1`,
      texture_color_prefix: getDefaultTexturePrefixByIndex(0),
      texture_normal_prefix: getDefaultTexturePrefixByIndex(1),
      texture_pbr_prefix: getDefaultTexturePrefixByIndex(2),
      texture_emissive_prefix: getDefaultTexturePrefixByIndex(3),
      texture_occlusion_prefix: getDefaultTexturePrefixByIndex(4),
      texture_hsvMask_prefix: getDefaultTexturePrefixByIndex(5),
      shader_prefix: '',
      shader_file_path: '',
      part_one_name: '部位 1',
      part_two_name: '部位 2',
      part_three_name: '部位 3',
      part_four_name: '部位 4',
      part_one_name_en: 'part 1',
      part_two_name_en: 'part 2',
      part_three_name_en: 'part 3',
      part_four_name_en: 'part 4',
    }

    _.set(category, 'properties.model_material_list', [newMaterial])
  }
}

/**
 * 添加材质列表
 * @param category 当前分类信息
 * @param resource 资源信息
 * @param materialPath 当前编辑的材质路径，如果有值，则添加到对应的材质路径下，如果没有值，代表材质是模型内置材质
 * @param materialId 材质 id
 */
export function updateMaterialListByConfig({
  modelMaterialList,
  category,
  resource,
  materialPath,
  materialId,
}: {
  modelMaterialList: ModelMaterial[]
  category: CookedCategory
  resource: { [key: number]: Resource[] }
  materialPath?: string
  materialId: ModelResId
}): ModelMaterial[] {
  const tempModelMaterialList = _.cloneDeep(modelMaterialList)
  let is_default = false
  if (!materialId) {
    console.warn('没有材质 id，直接返回')
    return tempModelMaterialList
  }
  if (
    tempModelMaterialList.find(
      (item) => JSON.stringify(item.id) === JSON.stringify(materialId),
    )
  ) {
    console.warn('材质已存在，直接返回')
    return tempModelMaterialList
  }
  // 如果已经有路径，并且路径的资源的 id 是 5 位数的数字，代表是用户添加的外观材质，直接调整 id 即可
  if (
    materialPath &&
    tempModelMaterialList.find((item) => {
      return item.shader_file_path === materialPath
    }) &&
    isFeModelId(
      tempModelMaterialList.find((item) => {
        return item.shader_file_path === materialPath
      }).id,
    )
  ) {
    const targetMaterial = tempModelMaterialList.find((item) => {
      return item.shader_file_path === materialPath
    })
    targetMaterial.id = materialId
    return tempModelMaterialList
  }
  const textureResourceList = []
  _.each(resource, (value, key) => {
    if (value.some((res) => isTextureResource(res))) {
      textureResourceList.push(...value)
    }
  })
  const getDefaultTexturePrefixByIndex = (index: number) => {
    return (
      _.find(
        textureResourceList,
        (res) => getTargetTextureResourceIndex(res, category.prefix) === index,
      )?.prefix || ''
    )
  }
  const getShaderResPrefix = (): string => {
    if (!materialPath) return ''
    let prefix = ''
    _.each(resource, (value, key) => {
      if (value.some((res) => materialPath === res.real_file_path)) {
        prefix = value.find((res) => materialPath === res.real_file_path).prefix
      }
    })
    return prefix
  }
  // 没有材质路径，但是有 materialId 代表是模型内置材质, 内置材质在 rebuild-category 的时候已经赋值，所以这里只需要调整 id 即可
  if (!materialPath) {
    is_default = true
    const targetMaterial = tempModelMaterialList.find((item) => {
      return item.is_default === is_default
    })
    targetMaterial.id = materialId
    return tempModelMaterialList
  } else {
    tempModelMaterialList.push({
      id: materialId,
      is_default,
      name: `皮肤 ${tempModelMaterialList.length + 1}`,
      name_en: `skin ${tempModelMaterialList.length + 1}`,
      texture_color_prefix: getDefaultTexturePrefixByIndex(0),
      texture_normal_prefix: getDefaultTexturePrefixByIndex(1),
      texture_pbr_prefix: getDefaultTexturePrefixByIndex(2),
      texture_emissive_prefix: getDefaultTexturePrefixByIndex(3),
      texture_occlusion_prefix: getDefaultTexturePrefixByIndex(4),
      texture_hsvMask_prefix: getDefaultTexturePrefixByIndex(5),
      shader_prefix: getShaderResPrefix(),
      shader_file_path: materialPath || '',
      part_one_name: '部位 1',
      part_two_name: '部位 2',
      part_three_name: '部位 3',
      part_four_name: '部位 4',
      part_one_name_en: 'part 1',
      part_two_name_en: 'part 2',
      part_three_name_en: 'part 3',
      part_four_name_en: 'part 4',
    })
  }

  return tempModelMaterialList
}

export function getMaterialListByDefault(index: number) {
  return {
    id: createFeModelId(),
    is_default: false,
    name: `皮肤 ${index + 1}`,
    name_en: `skin ${index + 1}`,
    texture_color_prefix: '',
    texture_normal_prefix: '',
    texture_pbr_prefix: '',
    texture_emissive_prefix: '',
    texture_occlusion_prefix: '',
    texture_hsvMask_prefix: '',
    shader_prefix: '',
    shader_file_path: '',
    part_one_name: '部位 1',
    part_two_name: '部位 2',
    part_three_name: '部位 3',
    part_four_name: '部位 4',
    part_one_name_en: 'part 1',
    part_two_name_en: 'part 2',
    part_three_name_en: 'part 3',
    part_four_name_en: 'part 4',
  }
}

export function getMaterialList(category: CookedCategory): ModelMaterial[] {
  return _.get(category, 'properties.model_material_list', []) || []
}

export function changeMaterialByIdAndKey(
  category: CookedCategory,
  materialId: ModelResId,
  key: string,
  value: any,
) {
  if (!category.properties?.model_material_list) {
    return
  }
  const index = category.properties.model_material_list.findIndex((item) => {
    return item.id === materialId
  })
  if (index !== -1) {
    Vue.set(category.properties.model_material_list[index], key, value)
  }
}

// 获取所有皮肤下，prefix 对应 key 的 prefix 值
export function getAllSkinTexturePrefix({
  prefix,
  modelMaterialList,
}: {
  prefix: string
  modelMaterialList: ModelMaterial[]
}): string[] {
  let targetKey: keyof ModelMaterial
  modelMaterialList.forEach((item: ModelMaterial) => {
    for (const key in item) {
      if (item[key] === prefix) {
        targetKey = key as keyof ModelMaterial
      }
    }
  })
  if (targetKey) {
    return _.uniq(modelMaterialList.map((item) => item[targetKey]))
  }
  return [prefix]
}

export function getAllSkinTexturePrefixByCategory(
  category: CookedCategory,
): string[] {
  const prefixes = [
    'texture_pbr_prefix',
    'texture_color_prefix',
    'texture_normal_prefix',
    'texture_emissive_prefix',
    'texture_occlusion_prefix',
    'texture_hsvMask_prefix',
  ]

  const uniquePrefixes = new Set<string>()

  getMaterialList(category).forEach((item) => {
    prefixes.forEach((prefix) => {
      if (item[prefix]) {
        uniquePrefixes.add(item[prefix])
      }
    })
  })

  return Array.from(uniquePrefixes)
}

export function getCurrentKeyByValue({
  prefix,
  modelMaterialList,
}: {
  prefix: string
  modelMaterialList: ModelMaterial[]
}): keyof ModelMaterial {
  let targetKey: keyof ModelMaterial
  modelMaterialList.forEach((item: ModelMaterial) => {
    for (const key in item) {
      if (item[key] === prefix) {
        targetKey = key as keyof ModelMaterial
      }
    }
  })
  return targetKey
}

export function syncModelMaterialListToCategory({
  modelMaterialList,
  category,
}: {
  modelMaterialList: ModelMaterial[]
  category: CookedCategory
}): void {
  _.set(category, 'properties.model_material_list', modelMaterialList)
}
